import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TYPE_META = {
  feedback: { color: "text-primary", label: "Feedback" },
  announcement: { color: "text-secondary", label: "Anuncio" },
  submission: { color: "text-accent", label: "Tarea" },
  quiz: { color: "text-secondary", label: "Quiz" },
  intencion: { color: "text-primary", label: "Intención" },
  password: { color: "text-primary", label: "Contraseña" },
};

function fmt(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "ahora";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
  } catch { return ""; }
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = async () => {
    try {
      const { data: res } = await api.get("/notifications");
      setData(res || { items: [], unread: 0 });
    } catch {
      setData({ items: [], unread: 0 });
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      await load();
    } catch {}
  };

  const markOneRead = async (id) => {
    try {
      await api.post(`/notifications/read/${encodeURIComponent(id)}`);
      setData((d) => ({
        ...d,
        items: d.items.map((it) => (it.id === id ? { ...it, read: true } : it)),
        unread: Math.max(0, d.unread - 1),
      }));
    } catch {}
  };

  useEffect(() => {
    if (!user || !user.user_id) return;
    load();
    const t = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  if (!user || !user.user_id) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full relative" data-testid="notif-bell" aria-label="Notificaciones">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-secondary text-secondary-foreground text-[10px] font-bold flex items-center justify-center"
              data-testid="notif-badge"
            >
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto" data-testid="notif-menu">
        <DropdownMenuLabel className="flex items-center justify-between gap-2">
          <span>Notificaciones</span>
          <div className="flex items-center gap-1">
            {data.unread > 0 && (
              <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={markAllRead} data-testid="notif-read-all">
                Marcar todas leídas
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={load} data-testid="notif-refresh">↻</Button>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {data.items.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-foreground/60" data-testid="notif-empty">
            No tienes notificaciones por ahora.
          </div>
        ) : (
          <ul className="py-1">
            {data.items.map((n) => {
              const meta = TYPE_META[n.type] || { color: "text-primary", label: "" };
              const inner = (
                <div
                  className={`px-3 py-2.5 hover:bg-muted/60 cursor-pointer transition-colors border-l-2 ${n.read ? "border-transparent opacity-60" : "border-secondary"}`}
                  data-testid={`notif-${n.id}`}
                  onClick={() => !n.read && markOneRead(n.id)}
                >
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={`text-[10px] tracking-[0.2em] uppercase font-bold ${meta.color}`}>{meta.label}</span>
                    <span className="text-[10px] text-foreground/50">{fmt(n.created_at)}</span>
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-tight">{n.title}</p>
                  {n.body && <p className="text-xs text-foreground/70 leading-snug mt-0.5 line-clamp-2">{n.body}</p>}
                </div>
              );
              if (n.topic_id) {
                return (
                  <li key={n.id}><Link to={`/tema/${n.topic_id}`}>{inner}</Link></li>
                );
              }
              return <li key={n.id}>{inner}</li>;
            })}
          </ul>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
